import { TableEditorConfig, Selection } from "./dto";
import { produce } from "immer";
import React, {
  useMemo,
  createContext,
  useRef,
  useContext,
  ReactNode,
} from "react";
import { createStore, useStore } from "zustand";

interface SettlementState {
  selection: Selection;
  config: TableEditorConfig;
  action: "select" | "resize" | "none";
}

export type StoreSetter<T> = (fn: T | ((d: T) => void | T)) => void;

type CreateObjectFromUnion<
  T extends Record<string, any>,
  U extends keyof T = keyof T
> = {
  [K in U as `set${Capitalize<string & K>}`]: StoreSetter<T[K]>;
} & {
  set: StoreSetter<T>;
};

const wrapperImmer = <T, P extends Function = Function>(
  set: P,
  key?: string
): StoreSetter<T> => {
  return (fn) => {
    if (typeof fn === "function") {
      if (key) {
        return set((d) => ({ ...d, [key]: produce(d[key], fn) }));
      }
      return set((d) => produce(d, fn));
    }
    if (key) {
      return set((d) => ({ ...d, [key]: fn }));
    }
    return set(fn);
  };
};

const createSettlementStore = () =>
  createStore<SettlementState & CreateObjectFromUnion<SettlementState>>(
    (set) => {
      return {
        selection: { cellIds: [] },
        config: {
          rows: [],
          cols: [],
          loops: [],
          formulas: [],
        },
        action: "none",
        set: wrapperImmer<SettlementState>(set),
        setSelection: wrapperImmer<SettlementState["selection"]>(
          set,
          "selection"
        ),
        setConfig: wrapperImmer<SettlementState["config"]>(set, "config"),
        setAction: wrapperImmer<SettlementState["action"]>(set, "action"),
      };
    }
  );

type SettlementStore = ReturnType<typeof createSettlementStore>;

const SettlementStoreContext = createContext<SettlementStore>(null);

const SettlementStoreProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<SettlementStore>();
  if (!storeRef.current) {
    storeRef.current = createSettlementStore();
  }
  return (
    <SettlementStoreContext.Provider value={storeRef.current}>
      {children}
    </SettlementStoreContext.Provider>
  );
};

const useSettlementStore = () => {
  const ctx = useContext(SettlementStoreContext);
  const { selection, config, action, ...setters } = useStore(ctx);
  const mode = useMemo(() => {
    const len = selection.cellIds.length;
    if (len === 1) return "single";
    if (len > 1) return "multiple";
    return "none";
  }, [selection.cellIds.length]);
  return [{ selection, config, action, mode }, setters] as const;
};

export default SettlementStoreProvider;

export { useSettlementStore };
